'use client';

import React, { useState, useEffect, useMemo } from 'react';
import Link from '@/core/i18n/navigation';
import NextImage from 'next/image';
import { Calendar, User, ArrowRight, Lock, ChevronLeft, ChevronRight, BookOpen } from 'lucide-react';
import { Card } from '@/core/components/ui/card';
import { Button } from '@/core/components/ui/button';
import { BlockProps } from './types';
import { cn } from '@/core/utils/cn';
import { fetchAPI } from '@/core/api/client';
import { toOptimizableSrc, canOptimizeImage } from '@/core/utils/media';
import { useTranslation } from '@/core/components/providers/TranslationProvider';
import { resolvePaginationAppearance, paginationItemStyle, paginationArrowStyle, paginationDotStyle, paginationContainerStyle, paginationHoverCss, paginationScopeClass } from '../shared/paginationAppearance';

const MOCK_DEMO_POSTS = [
  {
    id: 'mock-1',
    title: 'MUTABOX® - nowa wersja aplikacji z trybem Scan Game',
    slug: 'mock-mutabox-scan-game',
    kicker: 'Aplikacja',
    excerpt: 'Zupełnie nowa odsłona aplikacji z trybem rozgrywki dla wiecznie rywalizującego rodzeństwa i całych ekip dzieciaków. Zobacz krok po kroku, jak grać.',
    author_name: 'Anna Nowak',
    published_at: new Date('2026-07-02').toISOString(),
    is_featured: true,
    is_club_exclusive: false,
  },
  {
    id: 'mock-2',
    title: '5 najczęstszych błędów w żywieniu kotów sterylizowanych',
    slug: 'mock-zywienie-kotow',
    kicker: 'Dieta i żywienie',
    excerpt: 'Sterylizacja zmienia metabolizm kota, co wymaga dostosowania jego diety. Dowiedz się, na co zwracać szczególną uwagę, aby zapobiec nadwadze.',
    author_name: 'dr Jan Kowalski',
    published_at: new Date('2026-07-05').toISOString(),
    is_featured: false,
    is_club_exclusive: true,
  },
  {
    id: 'mock-3',
    title: 'Wycinanka rekyklingowa - darmowe arkusze do druku',
    slug: 'mock-wycinanka-druk',
    kicker: 'Do pobrania',
    excerpt: 'Pobierz nasz interaktywny plik PDF, wydrukuj go i twórz wyjątkowe zabawki razem z dziećmi z recyklingowanych materiałów.',
    author_name: 'Zespół Łappy',
    published_at: new Date('2026-07-04').toISOString(),
    is_featured: false,
    is_club_exclusive: true,
  },
  {
    id: 'mock-4',
    title: 'Alergie pokarmowe u psów - objawy i diagnostyka',
    slug: 'mock-alergie-psow',
    kicker: 'Zdrowie',
    excerpt: 'Jak rozpoznać alergię pokarmową u psa? Przeczytaj o najczęstszych alergenach, diecie eliminacyjnej oraz o tym, jak skutecznie pomóc swojemu psu.',
    author_name: 'lek. wet. Marta Wiśniewska',
    published_at: new Date().toISOString(),
    is_featured: false,
    is_club_exclusive: false,
  },
  {
    id: 'mock-5',
    title: 'Zabawki edukacyjne dla dzieci: Dlaczego warto ich używać?',
    slug: 'mock-zabawki-kot',
    kicker: 'Behawiorystyka',
    excerpt: 'Dzieci to mali odkrywcy, którzy potrzebują stymulacji umysłowej. Przedstawiamy najlepsze zabawki interaktywne i sensoryczne.',
    author_name: 'Katarzyna Lis',
    published_at: new Date().toISOString(),
    is_featured: false,
    is_club_exclusive: false,
  }
];

const DEFAULT_ELEMENT_ORDER = ['image', 'kicker', 'date', 'title', 'excerpt', 'author', 'button'];
// The block editor stores this literal Polish string as the seeded value when a merchant
// toggles the button on (it also doubles as the "show/hide" flag: '' = hidden, see
// BlogPostsEditor.tsx's `isElementVisible`/`toggleElementVisible`). Treat it as "never
// customized" so the visitor's locale wins instead of permanently pinning every storefront
// to Polish; anything else the merchant actually typed is kept verbatim in every locale.
const LEGACY_DEFAULT_BUTTON_TEXT = 'Czytaj';

/**
 * The published articles a block shows, newest (featured first) — or the hand-picked ones in their
 * order. Shared by the client fetch and the server-prefetched list (serverPrefetch.ts), so both
 * render the same cards.
 */
function selectArticles(
  pages: any[],
  opts: { queryType: 'latest' | 'manual'; manualArticleIds: number[]; categoryFilter: string; limit: number },
): any[] {
  let arts = pages.filter((p: any) => p.page_type === 'article' && p.status === 'published');
  if (opts.queryType === 'manual') {
    const byId = new Map(arts.map((p: any) => [p.id, p]));
    return opts.manualArticleIds.map(id => byId.get(id)).filter(Boolean);
  }
  if (opts.categoryFilter) {
    arts = arts.filter((p: any) => p.kicker && p.kicker.toLowerCase().includes(opts.categoryFilter.toLowerCase()));
  }
  arts.sort((a: any, b: any) => {
    const featA = a.is_featured ? 1 : 0;
    const featB = b.is_featured ? 1 : 0;
    if (featA !== featB) return featB - featA;
    return new Date(b.published_at || b.created_at).getTime() - new Date(a.published_at || a.created_at).getTime();
  });
  return arts.slice(0, opts.limit);
}

export function BlogPostsBlock({ block, currentStyles, isAdmin, viewport, initialData }: BlockProps) {
  const { t } = useTranslation();
  const [fetchedArticles, setArticles] = useState<any[]>([]);
  const [fetchLoading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  // Seeded from the renderer's viewport (the server's guess for the storefront, the editor's
  // frame in admin) so the server and the first client render agree on posts-per-page; the
  // resize listener below takes over after mount.
  const [isMobile, setIsMobile] = useState(viewport === 'mobile');
  const [isTablet, setIsTablet] = useState(viewport === 'tablet');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleResize = () => {
      const w = window.innerWidth;
      setIsMobile(w < 768);
      setIsTablet(w >= 768 && w < 1024);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const settings = block.settings || {};

  // Visibility
  const showImage   = settings.show_image   !== false;
  const showTitle   = settings.show_title   !== false;
  const showExcerpt = settings.show_excerpt !== false;
  const showKicker  = settings.show_kicker  !== false;
  const showDate    = settings.show_date    !== false;
  const showAuthor  = settings.show_author  !== false;
  const showPagination = settings.show_pagination === true;

  // Element Order
  const elementOrder: string[] = Array.isArray(settings.element_order) && settings.element_order.length > 0
    ? settings.element_order
    : DEFAULT_ELEMENT_ORDER;

  // Layout
  const columnsDesktop = settings.columns         || 3;
  const columnsTablet  = settings.columns_tablet  || 2;
  const columnsMobile  = settings.columns_mobile  || 1;
  const mobileLayout: 'grid' | 'slider' = settings.mobile_layout === 'slider' ? 'slider' : 'grid';
  const isSliderMode = mobileLayout === 'slider';
  const limit          = settings.limit && typeof settings.limit === 'number' ? settings.limit : 12;
  const postsPerPage       = settings.posts_per_page        && typeof settings.posts_per_page        === 'number' ? settings.posts_per_page        : 6;
  const postsPerPageMobile = settings.posts_per_page_mobile && typeof settings.posts_per_page_mobile === 'number' ? settings.posts_per_page_mobile : 3;
  const categoryFilter = settings.category || '';
  const queryType: 'latest' | 'manual' = settings.queryType === 'manual' ? 'manual' : 'latest';
  const manualArticleIds: number[] = Array.isArray(settings.manualArticleIds) ? settings.manualArticleIds : [];

  // Typography & Styling Options
  const titleFontSize = currentStyles.title_font_size || settings.title_font_size || 'base'; // 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl'
  const titleFontWeight = settings.title_font_weight || 'bold'; // 'normal' | 'medium' | 'semibold' | 'bold' | 'extrabold'
  const excerptFontSize = currentStyles.excerpt_font_size || settings.excerpt_font_size || 'xs';
  const metaFontSize = currentStyles.meta_font_size || settings.meta_font_size || '10px';
  const metaStyle = settings.meta_style || 'header'; // 'header' | 'badge' | 'footer'
  const buttonStyle = settings.button_style || 'link'; // 'link' | 'button' | 'outline'
  const imageAspectRatio = settings.image_aspect_ratio || 'video'; // 'video' | 'square' | 'portrait' | 'wide'

  // Pagination style
  const paginationPosition = settings.paginationPosition || 'center';
  const paginationStyle    = settings.paginationStyle    || 'numbers';
  // Shared pagination appearance (PaginationStyleFields). Unset fields keep the original
  // bg-indigo-600/text-slate-* classes below; the older paginationActiveColor/paginationColor
  // pair is still read as a fallback inside resolvePaginationAppearance.
  const pg = resolvePaginationAppearance(settings);
  const pgScope = paginationScopeClass(block.id);
  const pgHoverCss = paginationHoverCss(pgScope, pg);

  // Card appearance
  const cardBgColor      = settings.card_bg_color     || '';
  const cardBorderColor  = settings.card_border_color || settings.border_color || '';
  const rawBorderRadius  = currentStyles.card_border_radius !== undefined && currentStyles.card_border_radius !== ''
    ? currentStyles.card_border_radius
    : settings.card_border_radius !== undefined && settings.card_border_radius !== ''
    ? settings.card_border_radius
    : settings.border_radius;
  const cardBorderRadius = rawBorderRadius !== undefined && rawBorderRadius !== '' ? Number(rawBorderRadius) : 24;
  const rawBorderWidth   = currentStyles.card_border_width !== undefined && currentStyles.card_border_width !== ''
    ? currentStyles.card_border_width
    : settings.card_border_width !== undefined && settings.card_border_width !== ''
    ? settings.card_border_width
    : settings.border_width;
  const cardBorderWidth  = rawBorderWidth !== undefined && rawBorderWidth !== '' ? Number(rawBorderWidth) : undefined;
  const cardShadow       = settings.card_shadow        || 'sm';

  // Text colors
  const titleColor   = settings.title_color   || '';
  const excerptColor = settings.excerpt_color || '';
  const kickerColor  = settings.kicker_color  || '';
  const metaColor    = settings.meta_color    || '';

  // Button — settings.button_text is '' when the merchant explicitly hid the button via the
  // editor toggle, undefined when never touched (new block), or a custom label otherwise.
  const rawButtonText = settings.button_text;
  const buttonVisible = rawButtonText !== '';
  const buttonText = rawButtonText && rawButtonText !== LEGACY_DEFAULT_BUTTON_TEXT
    ? rawButtonText
    : t('blog.read_more', LEGACY_DEFAULT_BUTTON_TEXT);
  const btnBgColor      = settings.btn_bg_color      || '';
  const btnTextColor    = settings.btn_text_color    || '';
  const rawBtnBorderRadius = currentStyles.btn_border_radius !== undefined && currentStyles.btn_border_radius !== '' ? currentStyles.btn_border_radius : settings.btn_border_radius;
  const btnBorderRadius = rawBtnBorderRadius !== undefined && rawBtnBorderRadius !== '' ? Number(rawBtnBorderRadius) : 12;

  // Active posts per page (responsive)
  // Storefront must key off these local isMobile/isTablet flags, not the incoming `viewport`
  // prop — BlockRenderer buckets that at 640/1024px, while getGridClass's real CSS breakpoints
  // below are 768/1024px (md:/lg:), so using `viewport` here disagreed with the visible grid
  // for real widths 640-767px. The admin canvas simulation has no such CSS breakpoint at play
  // (getGridClass branches directly on the discrete `viewport` value), so admin keeps using it.
  const activeViewport     = isAdmin ? (viewport || 'desktop') : (isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop');
  const activePostsPerPage = activeViewport === 'mobile' ? postsPerPageMobile : postsPerPage;

  const manualArticleIdsKey = manualArticleIds.join(',');

  // Storefront pages prefetch the article list on the server (serverPrefetch.ts), so the cards are
  // in the HTML — no "Ładowanie czytelni…" spinner and no footer jump when they replace it
  // (paputypupila /blog: CLS 0.6, the first post image as a late LCP).
  const prefetchedArticles = useMemo(
    () => (!isAdmin && Array.isArray(initialData)
      ? selectArticles(initialData, { queryType, manualArticleIds, categoryFilter, limit })
      : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialData, isAdmin, queryType, manualArticleIdsKey, categoryFilter, limit],
  );
  const articles = prefetchedArticles ?? fetchedArticles;
  const loading = prefetchedArticles ? false : fetchLoading;

  useEffect(() => {
    if (prefetchedArticles) return;
    if (queryType === 'manual' && manualArticleIds.length === 0) {
      setArticles(isAdmin ? MOCK_DEMO_POSTS : []);
      setLoading(false);
      return;
    }
    fetchAPI('/pages')
      .then(pages => {
        if (Array.isArray(pages)) {
          const arts = selectArticles(pages, { queryType, manualArticleIds, categoryFilter, limit });
          setArticles(arts.length === 0 && isAdmin ? MOCK_DEMO_POSTS : arts);
        } else {
          throw new Error('Not an array');
        }
        setLoading(false);
      })
      .catch(() => {
        if (isAdmin) setArticles(MOCK_DEMO_POSTS);
        setLoading(false);
      });
  }, [categoryFilter, limit, isAdmin, queryType, manualArticleIdsKey, prefetchedArticles]);

  useEffect(() => {
    setCurrentPage(1);
  }, [categoryFilter, limit, activePostsPerPage]);

  // Outer styles
  const outerStyles: React.CSSProperties = {
    backgroundColor: currentStyles.backgroundColor,
    borderColor: currentStyles.borderColor,
    borderWidth: currentStyles.borderWidth !== undefined ? `${currentStyles.borderWidth}px` : undefined,
    borderStyle: currentStyles.borderWidth ? 'solid' : undefined,
    borderRadius: currentStyles.borderRadius !== undefined ? `${currentStyles.borderRadius}px` : undefined,
    color: currentStyles.textColor || currentStyles.color,
    textAlign: (currentStyles.textAlign || 'left') as any,
  };

  // Card styles
  const shadowMap: Record<string, string> = {
    none: '',
    sm:   'shadow-sm hover:shadow-md',
    md:   'shadow-md hover:shadow-lg',
    lg:   'shadow-lg hover:shadow-xl',
    xl:   'shadow-xl hover:shadow-2xl',
  };
  const cardShadowClass = shadowMap[cardShadow] || 'shadow-sm hover:shadow-md';
  const isBorderZero = cardBorderWidth === 0;
  const cardStyles: React.CSSProperties = {
    backgroundColor: cardBgColor || undefined,
    borderColor:     isBorderZero ? 'transparent' : (cardBorderColor || undefined),
    borderWidth:     cardBorderWidth !== undefined ? `${cardBorderWidth}px` : undefined,
    borderStyle:     isBorderZero ? 'none' : (cardBorderWidth !== undefined ? 'solid' : undefined),
    borderRadius:    `${cardBorderRadius}px`,
  };
  const btnCustomStyles: React.CSSProperties = {
    backgroundColor: btnBgColor || undefined,
    color:           btnTextColor || undefined,
    borderRadius:    `${btnBorderRadius}px`,
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-slate-400 space-y-2">
        <div className="w-8 h-8 rounded-full border-2 border-violet-500 border-t-transparent animate-spin" />
        <span className="text-xs font-semibold">Ładowanie czytelni...</span>
      </div>
    );
  }

  if (articles.length === 0) {
    return (
      <div className="text-center py-12 text-slate-400 italic border border-slate-100 rounded-2xl bg-white" style={outerStyles}>
        Brak opublikowanych artykułów w wybranej kategorii.
      </div>
    );
  }

  const totalPages = showPagination ? Math.ceil(articles.length / activePostsPerPage) : 1;

  const displayedArticles = showPagination
    ? articles.slice((currentPage - 1) * activePostsPerPage, currentPage * activePostsPerPage)
    : articles;

  // Grid class responsive & builder viewport aware
  const getGridClass = (d: number, t: number, m: number) => {
    if (isAdmin) {
      if (activeViewport === 'mobile') return m === 2 ? 'grid-cols-2' : 'grid-cols-1';
      if (activeViewport === 'tablet') return t === 1 ? 'grid-cols-1' : t === 3 ? 'grid-cols-3' : 'grid-cols-2';
      return d === 1 ? 'grid-cols-1' : d === 2 ? 'grid-cols-2' : d === 4 ? 'grid-cols-4' : 'grid-cols-3';
    }
    const mCls = m === 2 ? 'grid-cols-2' : 'grid-cols-1';
    const tCls = t === 1 ? 'md:grid-cols-1' : t === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2';
    const dCls = d === 1 ? 'lg:grid-cols-1' : d === 2 ? 'lg:grid-cols-2' : d === 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3';
    return `${mCls} ${tCls} ${dCls}`;
  };
  const gridColsClass = getGridClass(columnsDesktop, columnsTablet, columnsMobile);

  // Mobile "slider" layout: a touch-scrollable, snap-to-card horizontal row instead of
  // the stacked/grid columns — tablet and desktop keep the normal grid either way, only
  // the sub-768px layout changes. Admin preview branches on the discrete `activeViewport`
  // (same reasoning as getGridClass above); the live site uses one responsive className so
  // it's flex below `md:` and grid at `md:`/`lg:` without any JS/CSS-breakpoint mismatch.
  const tabletGridCls = columnsTablet === 1 ? 'md:grid-cols-1' : columnsTablet === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2';
  const desktopGridCls = columnsDesktop === 1 ? 'lg:grid-cols-1' : columnsDesktop === 2 ? 'lg:grid-cols-2' : columnsDesktop === 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3';
  const sliderContainerClass = 'flex overflow-x-auto snap-x snap-mandatory no-scrollbar -mx-1 px-1 pb-2';
  const outerGridClass = isAdmin
    ? (isSliderMode && activeViewport === 'mobile' ? sliderContainerClass : cn('grid', gridColsClass))
    : (isSliderMode
        ? cn(sliderContainerClass, 'md:grid md:overflow-visible md:mx-0 md:px-0 md:pb-0', tabletGridCls, desktopGridCls)
        : cn('grid', gridColsClass));
  const sliderCardClass = isSliderMode
    ? (isAdmin
        ? (activeViewport === 'mobile' ? 'w-[78%] shrink-0 snap-start' : '')
        : 'w-[78%] sm:w-[320px] shrink-0 snap-start md:w-auto md:shrink md:snap-align-none')
    : '';

  const paginationJustify =
    paginationPosition === 'left'  ? 'justify-start' :
    paginationPosition === 'right' ? 'justify-end'   :
    'justify-center';

  const builderPreviewMode = isAdmin && showPagination && totalPages <= 1;
  const previewPages   = builderPreviewMode ? 3 : totalPages;
  const previewCurrent = builderPreviewMode ? 1 : currentPage;

  const handlePrev = () => { if (!builderPreviewMode) setCurrentPage(p => Math.max(p - 1, 1)); };
  const handleNext = () => { if (!builderPreviewMode) setCurrentPage(p => Math.min(p + 1, totalPages)); };
  const handlePage = (n: number) => { if (!builderPreviewMode) setCurrentPage(n); };

  // Image aspect ratio helper
  const aspectClass =
    imageAspectRatio === 'square' ? 'aspect-square' :
    imageAspectRatio === 'portrait' ? 'aspect-[3/4]' :
    imageAspectRatio === 'wide' ? 'aspect-[21/9]' :
    'aspect-video';

  // Title typography classes
  const titleSizeCls =
    titleFontSize === 'xs' ? 'text-xs' :
    titleFontSize === 'sm' ? 'text-sm' :
    titleFontSize === 'lg' ? 'text-lg' :
    titleFontSize === 'xl' ? 'text-xl' :
    titleFontSize === '2xl' ? 'text-2xl' :
    'text-base';

  const titleWeightCls =
    titleFontWeight === 'normal' ? 'font-normal' :
    titleFontWeight === 'medium' ? 'font-medium' :
    titleFontWeight === 'semibold' ? 'font-semibold' :
    titleFontWeight === 'extrabold' ? 'font-extrabold' :
    'font-bold';

  const excerptSizeCls =
    excerptFontSize === 'sm' ? 'text-sm' :
    excerptFontSize === 'base' ? 'text-base' :
    'text-xs';

  const metaSizeCls =
    metaFontSize === 'xs' ? 'text-xs' :
    metaFontSize === 'sm' ? 'text-sm' :
    'text-[10px]';

  return (
    <div className="space-y-8 w-full p-6 transition-all duration-300" style={outerStyles}>
      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
      <div
        className={cn(outerGridClass, currentStyles?.gap === undefined && 'gap-6')}
        style={currentStyles?.gap !== undefined ? { gap: `${currentStyles.gap}px` } : undefined}
      >
        {displayedArticles.map((art: any) => {
          // Fixed time zone: the cards are server-rendered now, and a server in UTC would otherwise
          // print a different day than a browser in Warsaw for posts published around midnight —
          // a hydration mismatch.
          const dateStr = new Date(art.published_at || art.created_at).toLocaleDateString('pl-PL', {
            year: 'numeric', month: 'long', day: 'numeric', timeZone: 'Europe/Warsaw'
          }).toUpperCase();

          const renderItem = (itemKey: string) => {
            switch (itemKey) {
              case 'image':
                if (!showImage) return null;
                return (
                  <div key="image" className={cn('relative w-full overflow-hidden bg-gradient-to-br from-slate-800 to-slate-600 text-white shrink-0', aspectClass)}>
                    {art.og_image && (
                      canOptimizeImage(art.og_image) ? (
                        <NextImage
                          src={toOptimizableSrc(art.og_image)}
                          alt=""
                          fill
                          sizes={`(min-width: 768px) ${Math.round(100 / columnsDesktop)}vw, ${Math.round(100 / columnsMobile)}vw`}
                          className="object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={art.og_image}
                          alt=""
                          loading="lazy"
                          className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      )
                    )}
                    {!art.og_image && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-5 text-center">
                        <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/12 ring-1 ring-white/20">
                          <BookOpen className="h-5 w-5" aria-hidden="true" />
                        </span>
                        <span className="text-[10px] font-black uppercase tracking-[0.18em] text-white/85">
                          {art.kicker || t('nav.blog', 'Poradnik')}
                        </span>
                      </div>
                    )}
                    <div className={cn('absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity', art.og_image ? 'bg-black/20' : 'bg-black/5')} />
                    {metaStyle === 'badge' && (showKicker || art.is_club_exclusive) && (
                      <div className="flex justify-between items-center z-10 w-full gap-2 p-4 relative">
                        {showKicker && art.kicker && (
                          <span
                            className="bg-white/20 text-white text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded backdrop-blur-sm"
                            style={kickerColor ? { color: kickerColor } : undefined}
                          >
                            {art.kicker}
                          </span>
                        )}
                        {art.is_club_exclusive && (
                          <span className="bg-violet-500 text-white text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded ml-auto flex items-center gap-1 shadow-sm">
                            <Lock size={9} /> Klub
                          </span>
                        )}
                      </div>
                    )}
                    {art.is_featured && (
                      <span className="absolute bottom-3 left-3 bg-yellow-400 text-slate-950 text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded w-max shadow-sm z-10">
                        Wyróżniony
                      </span>
                    )}
                  </div>
                );

              case 'kicker':
              case 'date':
                // Handled together in header meta line if metaStyle === 'header' to avoid duplication
                return null;

              case 'title':
                if (!showTitle) return null;
                return (
                  <div key="title" className="px-5 pt-3 pb-1">
                    {/* Header meta line (Category · Date) as in screenshot */}
                    {metaStyle === 'header' && (showKicker || showDate) && (
                      <div className={cn('flex items-center gap-1.5 uppercase font-semibold tracking-wider mb-2 flex-wrap', metaSizeCls)}
                        style={{ color: kickerColor || metaColor || '#64748b' }}>
                        {showKicker && art.kicker && (
                          <span className="font-bold" style={kickerColor ? { color: kickerColor } : undefined}>
                            {art.kicker}
                          </span>
                        )}
                        {showKicker && art.kicker && showDate && <span className="opacity-60">·</span>}
                        {showDate && (
                          <span style={metaColor ? { color: metaColor } : undefined}>
                            {dateStr}
                          </span>
                        )}
                      </div>
                    )}

                    {isAdmin ? (
                      <h3 className={cn('line-clamp-2 leading-snug cursor-not-allowed', titleSizeCls, titleWeightCls)}
                        style={titleColor ? { color: titleColor } : { color: '#1e293b' }}>
                        {art.title}
                      </h3>
                    ) : (
                      <Link href={`/blog/${art.slug}`}>
                        <h3 className={cn('line-clamp-2 leading-snug transition-colors group-hover:text-blue-600', titleSizeCls, titleWeightCls)}
                          style={titleColor ? { color: titleColor } : { color: '#1e293b' }}>
                          {art.title}
                        </h3>
                      </Link>
                    )}
                  </div>
                );

              case 'excerpt':
                if (!showExcerpt) return null;
                return (
                  <div key="excerpt" className="px-5 py-2 flex-1">
                    <p className={cn('leading-relaxed line-clamp-3', excerptSizeCls)}
                      style={{ color: excerptColor || '#64748b' }}>
                      {art.excerpt || 'Kliknij, aby przeczytać wpis.'}
                    </p>
                  </div>
                );

              case 'author':
                if (!showAuthor || !art.author_name || metaStyle === 'header') return null;
                return (
                  <div key="author" className="px-5 py-1 text-[10px] font-medium flex items-center gap-1" style={{ color: metaColor || '#64748b' }}>
                    <User size={11} className="shrink-0" />
                    <span className="truncate">{art.author_name}</span>
                  </div>
                );

              case 'button':
                if (!buttonVisible) return null;
                return (
                  <div key="button" className="px-5 pb-5 pt-2 flex items-center justify-between">
                    {buttonStyle === 'link' ? (
                      isAdmin ? (
                        <span className="inline-flex items-center gap-1.5 font-bold text-xs cursor-not-allowed text-slate-900 group-hover:text-blue-600 transition-colors"
                          style={btnTextColor ? { color: btnTextColor } : undefined}>
                          {buttonText} <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                        </span>
                      ) : (
                        <Link href={`/blog/${art.slug}`}
                          className="inline-flex items-center gap-1.5 font-bold text-xs text-slate-900 hover:text-blue-600 group-hover:text-blue-600 transition-colors"
                          style={btnTextColor ? { color: btnTextColor } : undefined}>
                          {buttonText} <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                        </Link>
                      )
                    ) : (
                      isAdmin ? (
                        <Button variant={buttonStyle === 'outline' ? 'outline' : 'default'} size="sm"
                          className="h-8 text-xs font-bold gap-1 cursor-not-allowed opacity-90"
                          style={btnBgColor || btnTextColor ? btnCustomStyles : undefined}>
                          {buttonText}
                        </Button>
                      ) : (
                        <Link href={`/blog/${art.slug}`}>
                          <Button variant={buttonStyle === 'outline' ? 'outline' : 'default'} size="sm"
                            className="h-8 text-xs font-bold gap-1 transition-all"
                            style={btnBgColor || btnTextColor ? btnCustomStyles : undefined}>
                            {buttonText}
                            <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
                          </Button>
                        </Link>
                      )
                    )}
                  </div>
                );

              default:
                return null;
            }
          };

          return (
            <div
              key={art.id}
              className={cn(
                isBorderZero ? '!border-0 !border-none' : (cardBorderWidth !== undefined ? 'border-solid' : 'border'),
                'overflow-hidden flex flex-col justify-between group transition-all duration-300 p-0',
                !cardBgColor && 'bg-white',
                !isBorderZero && !cardBorderColor && cardBorderWidth === undefined && 'border border-slate-100 hover:border-blue-200',
                cardShadowClass,
                sliderCardClass
              )}
              style={cardStyles}
            >
              <div className="flex flex-col h-full justify-between">
                {elementOrder.map((key) => renderItem(key))}
              </div>
            </div>
          );
        })}
      </div>

      {/* PAGINATION */}
      {showPagination && (totalPages > 1 || builderPreviewMode) && (
        <div className={cn('flex items-center gap-2 pt-6 border-t border-slate-100/50 flex-wrap', paginationJustify, pgScope)} style={paginationContainerStyle(pg)}>
          {pgHoverCss && <style>{pgHoverCss}</style>}
          {builderPreviewMode && (
            <span className="w-full text-center text-[9px] font-bold text-slate-300 uppercase tracking-widest mb-1 select-none">
              Podgląd paginacji (builder)
            </span>
          )}

          {paginationStyle === 'arrows' ? (
            <>
              <Button variant="outline" onClick={handlePrev} disabled={previewCurrent === 1} data-pg-item
                className="h-9 px-4 text-xs font-bold rounded-xl border-slate-200 disabled:opacity-40 gap-1"
                style={paginationArrowStyle(pg, true)}>
                <ChevronLeft size={14} /> {t('pagination.prev', 'Poprzednia')}
              </Button>
              <span className="text-[11px] text-slate-500 font-medium px-1" style={{ color: pg.text, fontSize: pg.fontSize }}>
                {previewCurrent} / {previewPages}
              </span>
              <Button variant="outline" onClick={handleNext} disabled={previewCurrent === previewPages} data-pg-item
                className="h-9 px-4 text-xs font-bold rounded-xl border-slate-200 disabled:opacity-40 gap-1"
                style={paginationArrowStyle(pg, true)}>
                {t('pagination.next', 'Następna')} <ChevronRight size={14} />
              </Button>
            </>
          ) : paginationStyle === 'dots' ? (
            <>
              <Button variant="ghost" size="icon" onClick={handlePrev} disabled={previewCurrent === 1}
                className="w-7 h-7 rounded-full disabled:opacity-40" style={{ color: pg.text }}>
                <ChevronLeft size={14} />
              </Button>
              {Array.from({ length: previewPages }, (_, i) => i + 1).map((n) => (
                <button key={n} onClick={() => handlePage(n)}
                  className={cn('rounded-full transition-all duration-200',
                    previewCurrent === n ? 'w-5 h-5 bg-indigo-600 shadow-sm' : 'w-2.5 h-2.5 bg-slate-300 hover:bg-slate-400'
                  )}
                  style={paginationDotStyle(pg, previewCurrent === n)}
                />
              ))}
              <Button variant="ghost" size="icon" onClick={handleNext} disabled={previewCurrent === previewPages}
                className="w-7 h-7 rounded-full disabled:opacity-40" style={{ color: pg.text }}>
                <ChevronRight size={14} />
              </Button>
            </>
          ) : (
            // NUMBERS (default)
            <>
              <Button variant="outline" size="icon" onClick={handlePrev} disabled={previewCurrent === 1} data-pg-item
                className="w-8 h-8 rounded-lg border-slate-200 text-slate-500 disabled:opacity-40 hover:bg-slate-50 shrink-0"
                style={paginationArrowStyle(pg)}>
                <ChevronLeft size={16} />
              </Button>
              {Array.from({ length: previewPages }, (_, i) => i + 1).map((n) => (
                <Button key={n}
                  variant={previewCurrent === n ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handlePage(n)}
                  data-pg-item
                  data-pg-active={previewCurrent === n ? 'true' : undefined}
                  aria-current={previewCurrent === n ? 'page' : undefined}
                  className={cn('w-8 h-8 rounded-lg text-xs font-bold shrink-0',
                    previewCurrent === n
                      ? 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm'
                      : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                  )}
                  style={paginationItemStyle(pg, previewCurrent === n)}>
                  {n}
                </Button>
              ))}
              <Button variant="outline" size="icon" onClick={handleNext} disabled={previewCurrent === previewPages} data-pg-item
                className="w-8 h-8 rounded-lg border-slate-200 text-slate-500 disabled:opacity-40 hover:bg-slate-50 shrink-0"
                style={paginationArrowStyle(pg)}>
                <ChevronRight size={16} />
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
